// Step5ImageUpload.tsx
import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { AiOutlineCloudUpload, AiOutlineClose } from "react-icons/ai";

interface FileType {
  id: string; // 고유 식별자 추가
  name: string;
  size: number; // KB 단위
  progress: number;
  status: 'uploading' | 'completed';
  isThumbnail: boolean;
  preview: string;
}

interface Step4ImageUploadProps {
  files: FileType[];
  handleMainImageChange: (newFiles: File[]) => Promise<void>;
  handleRemoveFile: (fileId: string) => void;
  handleSetThumbnail: (fileId: string) => void;
  errors: { [key: string]: string };
}

const Step5ImageUpload: React.FC<Step4ImageUploadProps> = ({
                                                             files,
                                                             handleMainImageChange,
                                                             handleRemoveFile,
                                                             handleSetThumbnail,
                                                             errors,
                                                           }) => {

  // react-dropzone 훅 사용
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      handleMainImageChange(acceptedFiles);
    },
    [handleMainImageChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': [],
      'application/pdf': ['.pdf'],
      'video/mp4': ['.mp4'],
    },
    maxSize: 50 * 1024 * 1024, // 50MB
    multiple: true,
  });

  return (
    <div className="w-full px-4">
      <label htmlFor="mainImage" className="block font-medium mb-1">
        이미지 업로드 <span className="text-red-500">*</span>
      </label>
      <p className="text-gray-500 text-sm mb-2">
        상품 이미지를 업로드 해주세요. 첫 번째 이미지는 썸네일로 사용됩니다.
      </p>

      {/* 드래그 앤 드롭 섹션 */}
      <div
        {...getRootProps()}
        className={`mt-4 border-dashed border-2 rounded-md p-4 cursor-pointer ${
          isDragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
        }`}
      >
        <input {...getInputProps()} id="mainImage" />
        <div className="flex justify-center items-center flex-col">
          <p className="text-gray-600 text-center mt-2">
            {isDragActive ? '파일을 놓아주세요...' : '파일을 선택하거나 여기에 드래그 앤 드롭하세요.'}
          </p>
          <p className="text-sm text-gray-400 mb-2">JPEG, PNG, PDF, MP4 포맷, 최대 50MB.</p>
          <button
            type="button"
            className="bg-gray-100 px-4 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-200"
            onClick={(e) => {
              e.stopPropagation(); // Dropzone 트리거 방지
              document.getElementById('mainImage')?.click();
            }}
          >
            파일 선택
          </button>
        </div>
      </div>

      {/* 에러 메시지 */}
      {errors.mainImage && (
        <p className="text-red-500 text-sm mt-1">{errors.mainImage}</p>
      )}

      {/* 파일 리스트 - 최대 3개 표시 및 스크롤 가능하도록 수정 */}
      <div className="mt-4 space-y-4 max-h-60 overflow-y-auto">
        {files.map((file) => (
          <div key={file.id} className="flex items-center justify-between p-2 border-b border-gray-200">
            <div className="flex items-center">
              {/* 이미지 또는 파일 아이콘 표시 */}
              {file.preview && file.preview.startsWith('data:image') ? (
                <img
                  src={file.preview}
                  alt={file.name}
                  className="w-16 h-16 object-cover rounded mr-3"
                />
              ) : file.preview && file.preview.endsWith('.pdf') ? (
                <img
                  src="/pdf-icon.png" // PDF 아이콘 경로 (프로젝트에 맞게 경로 수정 필요)
                  alt={file.name}
                  className="w-16 h-16 object-contain rounded mr-3"
                />
              ) : file.preview && file.preview.endsWith('.mp4') ? (
                <video
                  src={file.preview}
                  className="w-16 h-16 object-cover rounded mr-3"
                  muted
                  loop
                />
              ) : (
                <AiOutlineCloudUpload size={40} className="text-gray-500 mr-3" />
              )}
              <div>
                <div className="flex items-center">
                  <p className="font-medium text-gray-700">{file.name}</p>
                  {file.isThumbnail && (
                    <span className="ml-2 px-2 py-0.5 bg-blue-500 text-white text-xs rounded">썸네일</span>
                  )}
                </div>
                {/* 파일 크기를 직접 표시 */}
                <p className="text-sm text-gray-500">{file.size} KB</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* 업로드 진행 상태 */}
              {file.status === 'uploading' && (
                <>
                  <p className="text-sm text-gray-500">{file.progress}%</p>
                  <div className="w-24 bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full"
                      style={{ width: `${file.progress}%` }}
                    ></div>
                  </div>
                </>
              )}
              {file.status === 'completed' && (
                <p className="text-sm text-green-600">업로드 완료</p>
              )}

              {/* 썸네일 변경 버튼 */}
              {!file.isThumbnail && file.status === 'completed' && (
                <button
                  type="button"
                  className="text-blue-500 hover:text-blue-700 text-sm"
                  onClick={() => handleSetThumbnail(file.id)}
                >
                  썸네일로 지정
                </button>
              )}

              {/* 파일 제거 버튼 */}
              <button
                type="button"
                className="text-red-500 hover:text-red-700"
                onClick={() => handleRemoveFile(file.id)}
              >
                <AiOutlineClose size={20} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Step5ImageUpload;
